import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Clapperboard, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister ? { email, password, name } : { email, password };
      const { data } = await api.post(path, payload);
      await login(data.token, data.user);
      toast.success(isRegister ? "Welcome to Clipstar!" : "Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grain">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center amber-glow">
            <Clapperboard className="w-6 h-6 text-zinc-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-extrabold text-2xl">Clipstar</span>
        </Link>
        <div className="bg-[#141419] border border-white/10 rounded-2xl p-8">
          <h1 className="font-display text-2xl font-bold mb-1">
            {isRegister ? "Create your free account" : "Log in to Clipstar"}
          </h1>
          <p className="text-zinc-500 text-sm mb-6">
            {isRegister ? "Browse and save campaigns free — no card required." : "Continue clipping."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-zinc-900 border-white/10 mt-1.5"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-zinc-900 border-white/10 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-zinc-900 border-white/10 mt-1.5"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? "Create Account" : "Log In"}
            </Button>
          </form>
          <p className="text-center text-sm text-zinc-500 mt-6">
            {isRegister ? "Already have an account? " : "New to Clipstar? "}
            <Link
              to={isRegister ? "/login" : "/register"}
              data-testid="auth-switch-link"
              className="text-amber-400 hover:text-amber-300 font-medium"
            >
              {isRegister ? "Log in" : "Create one free"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
